import {
    approve,
    close,
    comment,
    deny,
    markProvisionComplete,
    markProvisionErrored,
    escalateToEmergencyAccess,
    reassign,
    restartTask,
    TasksAction,
    TasksActions,
    TaskValue,
} from 'lib/actions/task'
import { Status } from 'lib/rpc'
import { TaskActionsServiceClient } from 'pbts/c1/api/task/v1/actions.client'
import { TaskActionType, TaskType } from 'pbts/c1/api/task/v1/task'
import { Dispatch } from 'react'
import { IntlShape } from 'react-intl'

export enum Decision {
    Approve = 'approve',
    Comment = 'comment',
    Deny = 'deny',
    Cancel = 'cancel',
    Reassign = 'reassign',
    Restart = 'restart',
    ProvisionComplete = 'complete',
    ProvisionErrored = 'error',
    EscalateToEmergencyAccess = 'escalateToEmergencyAccess',
}

export type Action = {
    decision: Decision
    comment: string
    policyStepId?: string
    reassignedUserIDs?: string[]
}

export const handleTaskAction = (
    dispatch: Dispatch<TasksActions>,
    action: Action,
    taskSvc: TaskActionsServiceClient,
    task: TaskValue,
): void => {
    if (!task) {
        return
    }

    dispatch({
        type: TasksAction.process,
        payload: {
            status: Status.OK,
            item: task,
        },
    })

    switch (action.decision) {
        case Decision.Approve:
            approve(dispatch, taskSvc, task?.id || '', action)
            break
        case Decision.Deny:
            deny(dispatch, taskSvc, task?.id || '', action)
            break
        case Decision.Comment:
            comment(dispatch, taskSvc, task?.id || '', action.comment)
            break
        case Decision.Cancel:
            close(dispatch, taskSvc, task?.id || '', action)
            break
        case Decision.Reassign:
            reassign(dispatch, taskSvc, task?.id || '', action)
            break
        case Decision.Restart:
            restartTask(dispatch, taskSvc, task?.id || '', action)
            break
        case Decision.ProvisionComplete:
            markProvisionComplete(dispatch, taskSvc, task?.id || '', action)
            break
        case Decision.ProvisionErrored:
            markProvisionErrored(dispatch, taskSvc, task?.id || '', action)
            break
        case Decision.EscalateToEmergencyAccess:
            escalateToEmergencyAccess(dispatch, taskSvc, task?.id || '', action)
        // no default
    }
}

export const taskActionFromString = (action: string): TaskActionType => {
    switch (action) {
        case Decision.Approve:
            return TaskActionType.APPROVE
        case Decision.Comment:
            return TaskActionType.COMMENT
        case Decision.Deny:
            return TaskActionType.DENY
        case Decision.Reassign:
            return TaskActionType.REASSIGN
        case Decision.Restart:
            return TaskActionType.RESTART
        case Decision.EscalateToEmergencyAccess:
            return TaskActionType.ESCALATE_TO_EMERGENCY_ACCESS
        default:
            return TaskActionType.UNSPECIFIED
    }
}

export const taskActionToActionableIntlString = (action: TaskActionType, taskType: TaskType, intl: IntlShape): string => {
    const tt = taskType.taskType?.oneofKind

    switch (action) {
        case TaskActionType.APPROVE:
            switch (tt) {
                case 'certify':
                    return intl.formatMessage({
                        id: 'components_task_action_certify',
                        defaultMessage: 'Certify',
                    })
                case 'revoke':
                    return intl.formatMessage({
                        id: 'components_task_action_confirm',
                        defaultMessage: 'Confirm',
                    })
                case 'grant':
                    return intl.formatMessage({
                        id: 'components_task_action_grant',
                        defaultMessage: 'Grant',
                    })
                default:
                    return intl.formatMessage({
                        id: 'components_task_action_unknown_type',
                        defaultMessage: 'Unsupported task type: {tt}',
                    }, { tt: tt || 'unknown' })
            }
        case TaskActionType.DENY:
            switch (tt) {
                case 'certify':
                    return intl.formatMessage({
                        id: 'components_task_action_decertify',
                        defaultMessage: 'Deny',
                    })
                case 'revoke':
                    return intl.formatMessage({
                        id: 'components_task_action_decline',
                        defaultMessage: 'Decline',
                    })
                case 'grant':
                    return intl.formatMessage({
                        id: 'components_task_action_deny',
                        defaultMessage: 'Deny',
                    })
                default:
                    return intl.formatMessage({
                        id: 'components_task_action_unknown_type',
                        defaultMessage: 'Unsupported task type: {tt}',
                    }, { tt: tt || 'unknown' })
            }
        default:
            return intl.formatMessage({
                id: 'components_task_action_unknown',
                defaultMessage: 'Unsupported action: {action}',
            }, { action: action || 'unknown' })
    }
}
